import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProvider } from '../lib/provider';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { CheckCircle2, XCircle, Gift, Trophy } from 'lucide-react';

export const ParentDashboard: React.FC = () => {
    const provider = useProvider();
    const { session } = useAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const { data: members, isLoading: loadingMembers } = useQuery({
        queryKey: ['members', session.user?.household_id],
        queryFn: () => provider.getMembers(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const { data: missedDailyQuests, isLoading: loadingMissedDailyQuests } = useQuery({
        queryKey: ['missed-daily-quests', session.user?.household_id],
        enabled: !!session.user?.household_id && !!members,
        queryFn: async () => {
            const now = new Date();
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);

            const startOfYesterday = new Date(yesterday);

            const isSameDay = (a: Date, b: Date) => (
                a.getFullYear() === b.getFullYear() &&
                a.getMonth() === b.getMonth() &&
                a.getDate() === b.getDate()
            );

            const kids = (members || []).filter(m => m.role === 'kid');
            const results: Array<{
                id: string;
                kid_user_id: string;
                kid_display_name: string;
                quest_id: string;
                quest_title: string;
                quest_points: number;
                missed_deadline: string;
                planned_completed_at: string;
            }> = [];

            for (const kid of kids) {
                const [assigned, completions] = await Promise.all([
                    provider.getKidQuests(kid.user_id),
                    provider.getKidCompletions(kid.user_id),
                ]);

                for (const q of assigned || []) {
                    if (q.frequency !== 'daily') continue;
                    if (typeof (q as any).daily_end_hour !== 'number') continue;

                    const deadline = new Date(startOfYesterday);
                    deadline.setHours((q as any).daily_end_hour, 0, 0, 0);
                    if (now.getTime() <= deadline.getTime()) continue;

                    const hadAnyCompletionYesterday = (completions || []).some(c => {
                        if (c.quest_id !== q.id) return false;
                        const completedAt = new Date(c.completed_at);
                        if (Number.isNaN(completedAt.getTime())) return false;
                        if (!isSameDay(completedAt, startOfYesterday)) return false;
                        return true;
                    });

                    const wasApprovedAfterDeadline = (completions || []).some(c => {
                        if (c.quest_id !== q.id) return false;
                        if (c.status !== 'approved') return false;
                        const completedAt = new Date(c.completed_at);
                        if (Number.isNaN(completedAt.getTime())) return false;
                        return completedAt.getTime() > deadline.getTime();
                    });

                    if (hadAnyCompletionYesterday || wasApprovedAfterDeadline) continue;

                    results.push({
                        id: `${kid.user_id}::${q.id}::missed::${startOfYesterday.toISOString().slice(0, 10)}`,
                        kid_user_id: kid.user_id,
                        kid_display_name: kid.display_name,
                        quest_id: q.id,
                        quest_title: q.title,
                        quest_points: q.points,
                        missed_deadline: deadline.toISOString(),
                        planned_completed_at: deadline.toISOString(),
                    });
                }
            }

            return results;
        },
    });

    const { data: pendingCompletions, isLoading: loadingCompletions } = useQuery({
        queryKey: ['pending-completions', session.user?.household_id],
        queryFn: () => provider.getPendingCompletions(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const { data: pendingRedemptions, isLoading: loadingRedemptions } = useQuery({
        queryKey: ['pending-redemptions', session.user?.household_id],
        queryFn: () => provider.getPendingRedemptions(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const approveCompletion = useMutation({
        mutationFn: (id: string) => provider.approveCompletion(id, session.user!.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pending-completions'] });
        },
    });

    const rejectCompletion = useMutation({
        mutationFn: (id: string) => provider.rejectCompletion(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pending-completions'] });
        },
    });

    const approveAllCompletions = useMutation({
        mutationFn: async (ids: string[]) => {
            await Promise.all(ids.map((id) => provider.approveCompletion(id, session.user!.id)));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pending-completions'] });
        },
    });

    const approveMissedQuest = useMutation({
        mutationFn: async (payload: { questId: string; kidUserId: string; completedAt: string }) => {
            const completion = await provider.completeQuest({
                quest_id: payload.questId,
                kid_user_id: payload.kidUserId,
                completed_at: payload.completedAt,
            });
            await provider.approveCompletion(completion.id, session.user!.id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pending-completions'] });
            queryClient.invalidateQueries({ queryKey: ['missed-daily-quests'] });
            queryClient.invalidateQueries({ queryKey: ['kid-completions'] });
            queryClient.invalidateQueries({ queryKey: ['kid-history'] });
            queryClient.invalidateQueries({ queryKey: ['balance'] });
        },
        onError: (err: any) => {
            alert(err?.message || 'Failed to approve missed quest');
        },
    });

    const approveRedemption = useMutation({
        mutationFn: (id: string) => provider.approveRedemption(id, session.user!.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pending-redemptions'] });
        },
    });

    const rejectRedemption = useMutation({
        mutationFn: (id: string) => provider.rejectRedemption(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pending-redemptions'] });
        },
    });

    if (loadingMembers || loadingCompletions || loadingRedemptions || loadingMissedDailyQuests) {
        return <div className="p-8 text-center text-slate-500 font-bold">{t.common.loading}</div>;
    }

    return (
        <div className="space-y-8">
            {missedDailyQuests && missedDailyQuests.length > 0 && (
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <Trophy className="text-rose-500" />
                        <h2 className="text-2xl font-bold text-slate-900">Missed Quests</h2>
                    </div>

                    <div className="grid gap-4">
                        {missedDailyQuests.map((m) => (
                            <div key={m.id} className="bg-rose-50 rounded-xl p-6 border border-rose-200 flex items-center justify-between shadow-sm">
                                <div>
                                    <h3 className="font-bold text-lg text-slate-900">{m.quest_title}</h3>
                                    <p className="text-slate-600">{m.kid_display_name}</p>
                                    <p className="text-sm text-slate-400 mt-1">Missed by {new Date(m.missed_deadline).toLocaleString()}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="mr-4 text-right">
                                        <span className="text-primary-600 font-bold">+{m.quest_points} {t.common.pts}</span>
                                    </div>
                                    <button
                                        onClick={() => approveMissedQuest.mutate({ questId: m.quest_id, kidUserId: m.kid_user_id, completedAt: m.planned_completed_at })}
                                        disabled={approveMissedQuest.isPending}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:border-green-300 hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={t.common.approve}
                                    >
                                        <CheckCircle2 size={20} />
                                        <span className="font-semibold">{t.common.approve}</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section>
                <div className="flex items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-2">
                        <Trophy className="text-amber-500" />
                        <h2 className="text-2xl font-bold text-slate-900">{t.parentDashboard.questQueue}</h2>
                    </div>

                    <button
                        onClick={() => approveAllCompletions.mutate(pendingCompletions?.map((c) => c.id) ?? [])}
                        disabled={approveAllCompletions.isPending || !pendingCompletions?.length}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:border-green-300 hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t.parentDashboard.approveAll}
                    >
                        <CheckCircle2 size={20} />
                        <span className="font-semibold">{t.parentDashboard.approveAll}</span>
                    </button>
                </div>

                {pendingCompletions?.length === 0 ? (
                    <div className="bg-white rounded-xl p-8 border border-slate-200 text-center text-slate-500">
                        {t.parentDashboard.noPendingQuests}
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {pendingCompletions?.map((c) => (
                            <div key={c.id} className="bg-white rounded-xl p-6 border border-slate-200 flex items-center justify-between shadow-sm">
                                <div>
                                    <h3 className="font-bold text-lg text-slate-900">{c.quest.title}</h3>
                                    <p className="text-slate-600">{t.parentDashboard.completedBy} <span className="font-semibold text-slate-900">{c.member.display_name}</span></p>
                                    <p className="text-sm text-slate-400 mt-1">{new Date(c.completed_at).toLocaleString()}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="mr-4 text-right">
                                        <span className="text-primary-600 font-bold">+{c.quest.points} {t.common.pts}</span>
                                    </div>
                                    <button
                                        onClick={() => rejectCompletion.mutate(c.id)}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100 transition-colors"
                                        title={t.common.reject}
                                    >
                                        <XCircle size={20} />
                                        <span className="font-semibold">{t.common.reject}</span>
                                    </button>
                                    <button
                                        onClick={() => approveCompletion.mutate(c.id)}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:border-green-300 hover:bg-green-100 transition-colors"
                                        title={t.common.approve}
                                    >
                                        <CheckCircle2 size={20} />
                                        <span className="font-semibold">{t.common.approve}</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section>
                <div className="flex items-center gap-2 mb-4">
                    <Gift className="text-primary-500" />
                    <h2 className="text-2xl font-bold text-slate-900">{t.parentDashboard.rewardQueue}</h2>
                </div>

                {pendingRedemptions?.length === 0 ? (
                    <div className="bg-white rounded-xl p-8 border border-slate-200 text-center text-slate-500">
                        {t.parentDashboard.noPendingRewards}
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {pendingRedemptions?.map((r) => (
                            <div key={r.id} className="bg-white rounded-xl p-6 border border-slate-200 flex items-center justify-between shadow-sm">
                                <div>
                                    <h3 className="font-bold text-lg text-slate-900">{r.reward.title}</h3>
                                    <p className="text-slate-600"><span className="font-semibold text-slate-900">{r.member.display_name}</span> {t.parentDashboard.wantsToRedeem}</p>
                                    <p className="text-sm text-slate-400 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="mr-4 text-right">
                                        <span className="text-red-600 font-bold">-{r.reward.cost_points} {t.common.pts}</span>
                                    </div>
                                    <button
                                        onClick={() => rejectRedemption.mutate(r.id)}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100 transition-colors"
                                        title={t.common.reject}
                                    >
                                        <XCircle size={20} />
                                        <span className="font-semibold">{t.common.reject}</span>
                                    </button>
                                    <button
                                        onClick={() => approveRedemption.mutate(r.id)}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:border-green-300 hover:bg-green-100 transition-colors"
                                        title={t.common.approve}
                                    >
                                        <CheckCircle2 size={20} />
                                        <span className="font-semibold">{t.common.approve}</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};
